import Text from "app/styles/Text";
import React, { useEffect, useMemo } from "react";
import tw, { styled } from "twin.macro";
import EventActivity from "app/components/EventActivity";
import Button from "app/components/Button";
import { ReactComponent as ChainIcon } from "app/assets/icons/chain.svg";
import { COIN_IMG_BASE_URL, COMPLETED_STATUS } from "app/constants/variables";
import { useAppState } from "app/providers/AppStateProvider";
import { formatMoneyNoCurrency } from "app/lib/money";
import useRetrievePayment from "../hooks/useRetrievePayment";
import { formatCalendarTime } from "app/lib/date";
import { ReactComponent as CopyIcon } from "app/assets/icons/copy.svg";
import { capitalizeStr } from "app/lib/string";
import copyToClipboard from "app/lib/copyToClipboard";
import { Badge } from "app/components/Badge";
import ProcessingShimmer from "./ProcessingShimmer";
import LoaderContainer from "app/components/LoaderContainer";

const tabs = ["Transation status", "Transaction details"];

export default function ProcessingView({
  onContinue,
}: {
  onContinue: VoidFunction;
}) {
  const [activeTab, setActiveTab] = React.useState(0);
  const { merchant, payment, displayMode, parentOrigin, payment: paymentData } = useAppState();
  const [copied, setCopied] = React.useState<string | boolean>(false);
  const {
    isLoading,
    error,
    refetch,
  } = useRetrievePayment({
    id: payment?.id!,
    publicKey: merchant?.public_key,
  });

  // const totalFess = useMemo(() => {
  //   return paymentData?.fees?.reduce(
  //     (sum, fee) => sum + parseFloat(fee.amount.amount),
  //     0
  //   );
  // }, [paymentData?.fees]);

  const feesObject = useMemo(
    () =>
      paymentData?.fees.reduce((acc, item) => {
        const key = capitalizeStr(item.name.replace(/_/g, " "));
        acc[key] = (
          <Text semiBold>
            {`${formatMoneyNoCurrency(item.amount.amount, "digital")} ${
              item.amount.currency
            }`}
          </Text>
        );
        return acc;
      }, {} as Record<string, React.ReactNode>),
    [paymentData?.fees]
  );

  console.log(paymentData);

  const status =
    paymentData?.timeline?.events[paymentData?.timeline?.events?.length - 1]?.done;

  // useEffect(() => {
  //   if (
  //     Boolean(
  //       paymentData?.timeline.transfer_status === "funds_converted" ||
  //         paymentData?.timeline.transfer_status === "funds_received"
  //     )
  //   ) {
  //     if (!merchant.callback_url) {
  //       return;
  //     }

  //     const callbackUrl = new URL(merchant.callback_url);
  //     window.location.href = callbackUrl.toString();
  //   }
  // }, [paymentData?.timeline.transfer_status, merchant.callback_url]);

  useEffect(() => {
    if (
      Boolean(
        paymentData?.timeline.transfer_status === "funds_converted" ||
          paymentData?.timeline.transfer_status === "funds_received"
      )
    ) {
      if (merchant.callback_url) {
        const callbackUrl = new URL(merchant.callback_url);
        callbackUrl.searchParams.set("status", COMPLETED_STATUS.toLowerCase());
        callbackUrl.searchParams.set("paymentRequestId", paymentData?.id!);
        window.location.href = callbackUrl.toString();
      } else if (displayMode === "INLINE" && parentOrigin) {
        const target = window.parent;

        target.postMessage(
          {
            status: COMPLETED_STATUS,
            data: paymentData,
          },
          parentOrigin
        );

        return;
      }

      onContinue();
    }
  }, [displayMode, parentOrigin, paymentData, merchant.callback_url, onContinue]);

  const onCopy = (value: string) => {
    copyToClipboard(value).then(() => {
      setCopied(value);
    });
  };

  React.useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 1000);
    }
  }, [copied]);

  const displayRate = `${`1 ${
    paymentData?.source_currency
  } = ${formatMoneyNoCurrency(+paymentData?.rate?.rate!, "crypto")} ${
    paymentData?.target_currency
  }`}`;

  const details = {
    "Transaction ID": (
      <div tw="flex items-center gap-[8px]">
        <Text semiBold tw="max-w-[200px] truncate">
          {payment?.id}
        </Text>
        <button type="button" onClick={() => onCopy(paymentData?.id!)}>
          {copied === paymentData?.id ? (
            "Copied"
          ) : (
            <CopyIcon color="0D0D0D" tw="w-[20px] h-[20px]" />
          )}
        </button>
      </div>
    ),
    "Deposit address": (
      <div tw="flex items-center gap-[8px]">
        <Text semiBold tw="max-w-[200px] truncate">
          {payment?.pay_in.address}
        </Text>
        <button
          type="button"
          onClick={() => onCopy(paymentData?.pay_in?.address!)}
        >
          {copied === paymentData?.pay_in?.address ? (
            "Copied"
          ) : (
            <CopyIcon color="0D0D0D" tw="w-[20px] h-[20px]" />
          )}
        </button>
      </div>
    ),
    "Payment Method": <Text semiBold>{"Crypto wallet"}</Text>,
    Subtotal: (
      <Text semiBold>
        {formatMoneyNoCurrency(
          paymentData?.source_amount ? +paymentData?.source_amount : 0,
          "digital"
        )}{" "}
        {paymentData?.source_currency}
      </Text>
    ),
    ...feesObject,
    "Exchange rate": <Text semiBold>{displayRate}</Text>,
    Status: (
      <Badge variant={!status ? "warning" : "success"}>
        {status ? "Completed" : "Processing"}
      </Badge>
    ),
  };

  return (
    <LoaderContainer
      tw="h-full justify-start"
      loading={isLoading}
      errorMessage={error!}
      customLoader={<ProcessingShimmer />}
      onRetry={refetch}
    >
      <div tw="flex flex-col gap-[24px] h-full w-full">
        <div tw="flex items-center justify-between">
          <div tw="flex flex-col gap-[16px] w-full">
            <img
              src={`${COIN_IMG_BASE_URL}/${payment?.source_currency}`}
              alt={payment?.source_currency}
              tw="w-[40px] h-[40px] rounded-full mx-auto"
            />
            <div>
              <Text semiBold tw="text-[24px] text-center mb-1">
                Pay {formatMoneyNoCurrency(+payment?.source_amount!, "crypto")}{" "}
                {payment?.source_currency}
              </Text>
              <Text subdued tw="text-center">
                When {displayRate}
              </Text>
            </div>
          </div>
        </div>

        <div tw="bg-containment-tertiary rounded-[16px] p-[16px] flex flex-col gap-[24px]">
          <Tab>
            {tabs.map((item, index) => (
              <TabItem
                onClick={() => setActiveTab(index)}
                type="button"
                active={activeTab === index}
              >
                <p>{item}</p>
              </TabItem>
            ))}
          </Tab>

          {activeTab === 0 && (
            <>
              <div tw="flex flex-col gap-[16px]">
                {paymentData?.timeline.events.map((event, index) => (
                  <EventActivity
                    key={index}
                    title={event.title}
                    {...(event.done && {
                      description: event.description,
                      date: formatCalendarTime(event.timestamp),
                    })}
                    event={event.done ? "detected" : "completed"}
                  />
                ))}
              </div>

              {paymentData?.pay_in?.blockchain_url && (
                <div>
                  <Button
                    type="button"
                    // fullWidth
                    tw="mx-auto"
                    variant="secondary"
                    size="small"
                    onClick={() => {
                      window.open(
                        paymentData?.pay_in?.blockchain_url,
                        "_blank"
                      );
                    }}
                  >
                    <div tw="flex items-center gap-[8px]">
                      View on Blockchain Explorer
                      <ChainIcon tw="text-text-high" />
                    </div>
                  </Button>
                </div>
              )}
            </>
          )}

          {activeTab === 1 && (
            <div tw="flex flex-col gap-[24px]">
              {Object.keys(details).map((item) => (
                <div tw="flex  gap-[8px] items-center justify-between">
                  <Text tw="whitespace-nowrap" subdued>
                    {item}
                  </Text>
                  {details[item as keyof typeof details]}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </LoaderContainer>
  );
}

type Props = {
  active?: boolean;
};

const Tab = styled.div`
  ${tw`bg-containment-primary grid grid-cols-2 h-[36px] rounded-[999px] p-[4px]`}
`;

const TabItem = styled.button<Props>`
  ${tw`text-center h-full flex items-center justify-center  rounded-[999px]`}
  ${(props) => props.active && tw`bg-containment-tertiary`};

  p {
    ${tw`text-text-mid text-[14px]`}
    ${(props) => props.active && tw`font-semibold text-text-high`}
`;
